import { defineStore } from "pinia";
import axios from "@/app/plugins/axios";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import i18n from "@/app/plugins/i18n";
import { getErrorMessage } from "@/shared/utils/errorMessage";

// Define the interface for log items
export interface InventoryLogItem {
  id: string;
  action: string;
  productId: string;
  productName: string;
  priceId: string;
  priceNickname: string;
  priceName: string;
  productClass?: string;
  productClassName?: string;
  locationName?: string;
  referenceId: string;
  memo: string;
  paymentLinkId?: string;
  paymentLinkName?: string;
  paymentLinkUrl?: string;
  startingStock?: number;
  adjustedStock?: number;
  endingStock?: number;
  startingCheckout?: number;
  adjustedCheckout?: number;
  endingCheckout?: number;
  userId: string;
  userDisplayName: string;
  userEmail: string;
  createdAt: string;
}

// Define filter interface
export interface InventoryLogFilter {
  action?: string;
  userId?: string;
  startDate?: number;
  endDate?: number;
  referenceId?: string;
  productId?: string;
  productClassId?: string;
  priceId?: string;
  sku?: string;
}

// Define the store state interface
interface InventoryLogState {
  logs: InventoryLogItem[];
  loading: boolean;
  hasNext: boolean;
  hasPrev: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  filters: InventoryLogFilter;
  isExporting: boolean;
}

export interface InventoryLogPagination {
  limit?: number;
  page?: number;
  nextPage?: string;
}

export interface InventoryLogSearchResponse {
  data: InventoryLogItem[];
  hasNext?: boolean;
  hasPrev?: boolean;
  page?: number;
  total_pages?: number;
  total_items?: number;
}

export const useInventoryLogStore = defineStore("inventorylog", {
  state: (): InventoryLogState => ({
    logs: [],
    loading: false,
    hasNext: false,
    hasPrev: false,
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    filters: {},
    isExporting: false,
  }),

  getters: {
    isLoading: (state) => state.loading,
    getInventoryLogs: (state) => state.logs,
    getFilters: (state) => state.filters,
    hasFilters: (state) => Object.keys(state.filters).length > 0,
  },

  actions: {
    setFilter(key: keyof InventoryLogFilter, value: string | number | null | undefined) {
      if (value === null || value === undefined || value === "") {
        // If value is empty, remove the filter
        const { [key]: _, ...rest } = this.filters;
        this.filters = rest;
      } else {
        // Otherwise, set the filter
        this.filters = { ...this.filters, [key]: value };
      }

      // Automatically fetch logs with the new filters
      this.fetchInventoryLogs();
    },

    setFilters(newFilters: InventoryLogFilter) {
      // Replace filters with new filters instead of merging
      this.filters = { ...newFilters };
      this.fetchInventoryLogs();
    },

    clearFilters() {
      this.filters = {};
      this.fetchInventoryLogs();
    },

    async fetchInventoryLogs({ pagination = {} }: { pagination?: InventoryLogPagination } = {}) {
      try {
        this.loading = true;

        const payloadFilters: InventoryLogFilter = {};

        if (this.filters.action) {
          payloadFilters.action = this.filters.action;
        }

        if (this.filters.userId) {
          payloadFilters.userId = this.filters.userId;
        }

        if (this.filters.startDate) {
          payloadFilters.startDate = this.filters.startDate;
        }

        if (this.filters.endDate) {
          payloadFilters.endDate = this.filters.endDate;
        }

        if (this.filters.referenceId) {
          payloadFilters.referenceId = this.filters.referenceId;
        }

        if (this.filters.productId) {
          payloadFilters.productId = this.filters.productId;
        }

        if (this.filters.productClassId) {
          payloadFilters.productClassId = this.filters.productClassId;
        }

        if (this.filters.priceId) {
          payloadFilters.priceId = this.filters.priceId;
        }

        if (this.filters.sku) {
          payloadFilters.sku = this.filters.sku;
        }

        // Build payload with separate pagination and filters
        const payload = {
          pagination: {
            limit: 10, //default
            ...pagination,
          },
          filters: {
            ...payloadFilters,
          },
        };

        const response = await axios.post<InventoryLogSearchResponse>(
          "/inventorylog/search",
          payload,
        );
        // Update state with response data
        this.logs = response.data.data || [];
        this.hasNext = response.data.hasNext || false;
        this.hasPrev = response.data.hasPrev || false;
        this.currentPage = response.data.page || 1;
        this.totalPages = response.data.total_pages || 1;
        this.totalItems = response.data.total_items || 0;

        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({
          message: getErrorMessage(error, i18n.global.t("common.action.noResultsFound")),
        });
        return null;
      } finally {
        this.loading = false;
      }
    },

    // Export inventory logs based on current filters
    async exportLogs() {
      try {
        this.isExporting = true;

        // Validate date range first
        if (!this.filters.startDate || !this.filters.endDate) {
          useSnackBarStore().toastError({
            message: "Date range is required for export",
          });
          return;
        }

        // Verify date range isn't too large
        const normalizeToSeconds = (value: number) => {
          return value > 100000000000 ? Math.floor(value / 1000) : value;
        };

        const startDate = normalizeToSeconds(this.filters.startDate);
        const endDate = normalizeToSeconds(this.filters.endDate);
        const differenceInSeconds = endDate - startDate;
        const differenceInDays = differenceInSeconds / (60 * 60 * 24);

        if (differenceInDays > 30) {
          useSnackBarStore().toastError({
            message: "Date range cannot exceed 30 days",
          });
          return;
        }

        // Build filter payload
        const payloadFilters: Record<string, string | number> = {};

        Object.entries(this.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            payloadFilters[key] = value;
          }
        });

        // Create export payload without pagination to get all matching records
        const payload = {
          filters: payloadFilters,
          export: true, // Signal to the backend this is an export request
        };

        // Call the export endpoint
        const response = await axios.post("/inventorylog/export", payload, {
          responseType: "blob", // Important for handling file downloads
        });

        // Create a download link for the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        // Get the filename from the Content-Disposition header or use a default
        const contentDisposition = response.headers["content-disposition"];
        let filename = "inventory-log-export.csv";

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        useSnackBarStore().toastSuccess({
          message: "Inventory log export completed successfully",
        });
      } catch (error) {
        useSnackBarStore().toastError({
          message: getErrorMessage(error, "Failed to export inventory log data"),
        });
      } finally {
        this.isExporting = false;
      }
    },
  },
});
